import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { navigation } from '../../app-navigation';
import { AreaPermission } from 'src/app/types/dto/area-permission.dto';
import { AuthApiService } from './api/auth.api.service';
import { AuthService } from './auth.service';
import { UserTypeEnum } from 'src/app/types/dto/user.dto';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private authApiService = inject(AuthApiService);
  private authService = inject(AuthService);

  private navigationUpdated$ = new Subject<void>();

  navigationUpdated = this.navigationUpdated$.asObservable();

  constructor() {
    // Subscribe to currentUser and update navigation when user is loaded
    this.authService.currentUser.pipe(
      filter(user => !!user?.id)
    ).subscribe(() => {
      this.updateNavigation();
    });
  }

  private updateNavigation(): void {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      return;
    }

    this.authApiService.getAreaPermissions().subscribe({
      next: (permissions) => {
        this.applyNavigationVisibility(permissions, currentUser.isSuperUser ||  currentUser.userTypeId === UserTypeEnum.Admin);
      },
      error: (error) => {
        console.error('Error fetching area permissions:', error);
      }
    });
  }

  private applyNavigationVisibility(permissions: AreaPermission[], isSuperUser: boolean): void {
    const applicationsMenu = navigation.find(item => item.text === 'Applications');

    if (!applicationsMenu || !applicationsMenu.items) {
      return;
    }

    const setVisible = (name: string, visible: boolean) => {
      const item = applicationsMenu.items!.find(i => i.text === name);
      if (item) {
        item.visible = visible;
      }
    };

    if (isSuperUser) {
      // Super users can see all apps
      setVisible('Config', true);
      setVisible('Estimating', true);
      setVisible('Admin', true);
    } else {
      const optionListsPerm = this.getPermission(permissions, 'OptionLists');
      const estimatingPerm = this.getPermission(permissions, 'Estimating');
      const recipesPerm = this.getPermission(permissions, 'Recipes');

      // Config: OptionLists >= Write OR Estimating >= Write
      setVisible('Config', this.isWriteOrAbove(optionListsPerm) || this.isWriteOrAbove(estimatingPerm));

      // Estimating: Recipes >= Read OR OptionLists == Admin
      setVisible('Estimating', this.isReadOrAbove(recipesPerm) || this.isAdmin(optionListsPerm));
    }

    this.navigationUpdated$.next();
  }

  private getPermission(permissions: AreaPermission[], area: string): string | undefined {
    return permissions.find(p => p.applicationArea === area)?.permissionType;
  }

  private isReadOrAbove(permissionType: string | undefined): boolean {
    return permissionType === 'Read' || permissionType === 'Write' || permissionType === 'Admin';
  }

  private isWriteOrAbove(permissionType: string | undefined): boolean {
    return permissionType === 'Write' || permissionType === 'Admin';
  }

  private isAdmin(permissionType: string | undefined): boolean {
    return permissionType === 'Admin';
  }
}
